import styled from '@emotion/styled';
import { Button, Flex } from '@fluentui/react-northstar';
import { Redirect } from 'react-router-dom';
import { useUserContext } from '../contexts';
import { config } from '../config';
import { useEffect, useRef, useState, useCallback } from 'react';
import {
  app,
  authentication,
  HostClientType,
  pages,
} from '@microsoft/teams-js';
import { getAccessToken } from '../services/teamsBot';
import { logError } from '../utilities/ErrorLogger';

const MOBILE_CLIENT_TYPES: ReadonlySet<string> = new Set([
  HostClientType.ios,
  HostClientType.ipados,
  HostClientType.android,
]);

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 120_000;

const teamsAppIdStringFromContext = (raw: unknown): string | undefined => {
  if (raw === undefined || raw === null) {
    return undefined;
  }
  if (typeof raw === 'string') {
    return raw;
  }
  return String(raw);
};

const getTeamsAppIdFromContext = (
  context: Awaited<ReturnType<typeof app.getContext>>
): string | undefined => {
  const raw = (context.app as { appId?: unknown } | undefined)?.appId;
  return teamsAppIdStringFromContext(raw);
};

export const Landing = () => {
  const { teamsBotServiceUrl } = config;
  const { currentUser, azureAdId } = useUserContext();

  const [currentTabId, setCurrentTabId] = useState<string>('conversations');
  const [isMobileClient, setIsMobileClient] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const authResolvedRef = useRef(false);
  const pollErrorReportedRef = useRef(false);
  const installedTeamsAppIdRef = useRef<string | undefined>(undefined);

  const navigateToSameAppTab = useCallback(async (pageId: string) => {
    type CurrentAppApi = {
      isSupported: () => boolean;
      navigateTo: (params: { pageId: string }) => Promise<void>;
    };
    const currentApp = (
      pages as { currentApp?: CurrentAppApi }
    ).currentApp;

    try {
      if (currentApp?.isSupported?.()) {
        await currentApp.navigateTo({ pageId });
        return;
      }
    } catch (e) {
      console.error('pages.currentApp.navigateTo failed', e);
      logError(
        e instanceof Error
          ? e
          : new Error(`pages.currentApp.navigateTo failed: ${e}`)
      );
    }

    const targetAppId = installedTeamsAppIdRef.current ?? config.appId;

    try {
      await pages.navigateToApp({
        appId: targetAppId,
        pageId,
      });
    } catch (e) {
      console.error('pages.navigateToApp failed', e);
      logError(
        e instanceof Error
          ? e
          : new Error(`pages.navigateToApp failed: ${e}`)
      );
    }
  }, []);

  useEffect(() => {
    app
      .initialize()
      .then(() => app.getContext())
      .then((context) => {
        installedTeamsAppIdRef.current = getTeamsAppIdFromContext(context);
        setCurrentTabId(context.page.id);
        if (MOBILE_CLIENT_TYPES.has(context.app.host.clientType)) {
          setIsMobileClient(true);
        }
      })
      .catch((e) => {
        console.error('Landing: failed to initialize Teams SDK', e);
        logError(e instanceof Error ? e : new Error(String(e)));
      });
  }, []);

  // Poll for the token only on mobile. On desktop, authentication.authenticate()
  // owns the OAuth popup; polling here can call navigateToSameAppTab while the
  // popup is still open and break notifySuccess (parent loses the auth frame).
  useEffect(() => {
    if (!isAuthenticating || !azureAdId || !isMobileClient) return;

    const interval = setInterval(async () => {
      if (authResolvedRef.current) {
        clearInterval(interval);
        return;
      }
      try {
        await getAccessToken(azureAdId);
        authResolvedRef.current = true;
        clearInterval(interval);
        console.info('Landing: mobile auth poll succeeded, navigating to tab', {
          azureAdId,
          tabId: currentTabId,
        });
        await navigateToSameAppTab(currentTabId);
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status !== 401 && !pollErrorReportedRef.current) {
          pollErrorReportedRef.current = true;
          const err = e instanceof Error ? e : new Error(String(e));
          console.error('Landing: mobile auth poll unexpected error', {
            azureAdId,
            httpStatus: status,
          });
          logError(err);
        }
      }
    }, POLL_INTERVAL_MS);

    const timeout = setTimeout(() => {
      clearInterval(interval);
      if (!pollErrorReportedRef.current) {
        console.error('Landing: mobile auth poll timed out after 120s', {
          azureAdId,
        });
        logError(
          new Error(`Mobile auth poll timed out for azureAdId: ${azureAdId}`)
        );
      }
      setIsAuthenticating(false);
    }, POLL_TIMEOUT_MS);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [isAuthenticating, azureAdId, currentTabId, navigateToSameAppTab]);

  const startDesktopAuth = () => {
    setIsAuthenticating(true);
    authResolvedRef.current = false;
    pollErrorReportedRef.current = false;
    app.initialize().then(() => {
      authentication
        .authenticate({
          // `surface` is a provenance label, not an identity: it lets the
          // sign-in metric separate this tab's traffic (which identifies
          // itself reliably) from pre-fix cards that cannot.
          url: `${teamsBotServiceUrl}/oauth/kickoff?surface=tab`,
          width: 600,
          height: 535,
        })
        .then(async () => {
          authResolvedRef.current = true;
          console.info('Landing: desktop login succeeded, navigating to tab', {
            tabId: currentTabId,
          });
          await navigateToSameAppTab(currentTabId);
        })
        .catch(async (reason) => {
          authResolvedRef.current = true;
          setIsAuthenticating(false);
          console.error('Landing: desktop login failed', { reason });
          logError(
            reason instanceof Error
              ? reason
              : new Error(`Desktop login failed: ${JSON.stringify(reason)}`)
          );
        });
    }).catch((e) => {
      setIsAuthenticating(false);
      console.error('Landing: failed to initialize Teams SDK for auth', e);
      logError(e instanceof Error ? e : new Error(String(e)));
    });
  };

  if (currentUser) {
    return <Redirect to="/schedule" />;
  }

  // On mobile, render the login button as a plain <a> pointing at the
  // server-side redirect endpoint (/bot/oauth/start).  The user's tap is
  // a real gesture so iOS opens the URL in the system browser — no
  // WKWebView in-app overlay, no async fetch, no popup.
  const mobileAuthHref = azureAdId
    ? `${teamsBotServiceUrl}/oauth/start?teamsUserId=${encodeURIComponent(azureAdId)}`
    : undefined;

  return (
    <Flex
      hAlign="center"
      vAlign="center"
      gap="gap.small"
      column
      padding="padding.medium"
    >
      <BoldText>
        Hi! Please login to your Robin account to get started using Robin in
        Teams
      </BoldText>

      <SplashLogo src="https://static.robinpowered.com/reimagine/images/7eefbedab6290c5787d5a1c6c819bb0fe26571a1_1242.png" />

      {isMobileClient ? (
        <a
          href={mobileAuthHref}
          target="_blank"
          rel="noreferrer"
          onClick={() => {
            setIsAuthenticating(true);
            authResolvedRef.current = false;
            pollErrorReportedRef.current = false;
          }}
          style={{
            display: 'inline-block',
            padding: '8px 20px',
            color: 'white',
            backgroundColor: '#464EB8',
            borderRadius: '4px',
            textDecoration: 'none',
            fontSize: '14px',
            fontWeight: 600,
          }}
        >
          Log in to Robin
        </a>
      ) : (
        <Button
          style={{
            color: 'white',
            backgroundColor: '#464EB8',
          }}
          onClick={startDesktopAuth}
        >
          Log in to Robin
        </Button>
      )}
    </Flex>
  );
};

const BoldText = styled.div`
  font-family: Segoe UI;
  font-size: 18px;
  font-weight: 700;
  line-height: 24px;
  flex: 1;
`;

const SplashLogo = styled.img`
  height: 360px;
`;
